import React from 'react';
import { IComment } from './interfaces';
import {
  Avatar,
  Box,
  CardContent,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { ROUTES } from '@routes/routes';
import { formatLocaleDate } from '@helpers/dateHelpers';
import { Menu } from './components/Menu';
import { getCurrentUserAsMember } from '@helpers/memberHelper';
import { useTranslation } from 'react-i18next';

export const Comment: React.FC<IComment> = ({
  id,
  messageId,
  content,
  author,
  date,
  likesCount,
  likedByCurrentUser,
  onLikeClick,
}): JSX.Element => {
  const { t } = useTranslation();
  const currentUser = getCurrentUserAsMember();

  return (
    <CardContent
      sx={{
        py: '10px',
        position: 'relative',
        '&:hover .hover-actions': {
          opacity: 1,
          visibility: 'visible',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'start',
          gap: '10px',
        }}
      >
        <Avatar
          src={ROUTES.images.avatar(author.slug)}
          alt={author.name}
          sx={{
            width: '35px',
            height: '35px',
          }}
        />
        <Box
          sx={{
            width: '100%',
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignContent="center"
          >
            <Typography
              variant="body2"
              fontWeight="bold"
              sx={{
                mb: '3px',
              }}
            >
              {author.name}
            </Typography>
            <Menu
              messageId={messageId}
              commentId={id}
              currentUserIsAuthor={currentUser?.slug === author.slug}
            />
          </Stack>
          <Box
            sx={{
              backgroundColor: '#F9F9F9',
              p: '10px',
              borderRadius: '5px',
            }}
          >
            <Typography variant="body2">{content}</Typography>
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            gap="5px"
            sx={{
              mt: '2px',
            }}
          >
            <Link
              component="button"
              type="button"
              underline="hover"
              variant="body2"
              onClick={onLikeClick}
              sx={{
                fontWeight: likedByCurrentUser ? 700 : 400,
                color: likedByCurrentUser ? 'primary.main' : 'text.primary',
              }}
            >
              {likesCount > 0
                ? `${likesCount} ${t('feed:buttons.likes', {
                    count: likesCount,
                  })}`
                : t('feed:buttons.likes', { count: 1 })}
            </Link>
            <Divider
              component="div"
              sx={{
                color: 'text.secondary',
                span: { p: 0 },
              }}
            >
              ∙
            </Divider>
            <Typography variant="body2" color="text.secondary">
              {formatLocaleDate(date)}
            </Typography>
          </Stack>
        </Box>
      </Box>
    </CardContent>
  );
};
